import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import AIModuleTutorDrawer from "@/components/ai/AIModuleTutorDrawer";
import { useQAOverride } from "@/hooks/useQAOverride";
import AmbientWorldLayer from "./AmbientWorldLayer";
import ModuleRealmHeader from "./ModuleRealmHeader";
import MissionPanel from "./MissionPanel";
import { getRealm, type RealmKey } from "./lunimarRealms";

/**
 * Drop-in immersive replacement for `ModuleShell`, used by beta modules.
 *
 * Mirrors the original shell's props 1:1 so swapping is a single import
 * change plus a `realmKey`. Locked / completed / QA-preview behavior is
 * preserved verbatim — only the visual envelope changes.
 */
export interface LunimarModuleShellProps {
  realmKey: RealmKey;
  title: string;
  stepIndex: number;
  totalSteps: number;
  isLocked: boolean;
  lockMessage: string;
  isCompleted: boolean;
  completedMessage?: string;
  completedAction?: ReactNode;
  children: ReactNode;
  stepKey: string;
}

const LunimarModuleShell = ({
  realmKey,
  title,
  stepIndex,
  totalSteps,
  isLocked,
  lockMessage,
  isCompleted,
  completedMessage,
  completedAction,
  children,
  stepKey,
}: LunimarModuleShellProps) => {
  const navigate = useNavigate();
  const [showCoach, setShowCoach] = useState(false);
  const { isQAOverride } = useQAOverride();
  const realm = getRealm(realmKey);

  if (isLocked && !isQAOverride) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
        <AmbientWorldLayer realm={realm} />
        <div className="relative z-10 w-full max-w-md space-y-6 text-center">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border"
            style={{
              borderColor: `hsl(${realm.primaryAccent} / 0.3)`,
              background: `hsl(${realm.primaryAccent} / 0.08)`,
              color: `hsl(${realm.primaryAccent})`,
            }}
          >
            <Lock className="h-8 w-8" />
          </div>
          <h1
            className="font-heading text-xl font-bold"
            style={{ color: `hsl(${realm.primaryAccent})` }}
          >
            {title} — Locked
          </h1>
          <p className="text-sm leading-relaxed text-white/70">{lockMessage}</p>
          <Button variant="outline" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (isCompleted && !isQAOverride) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
        <AmbientWorldLayer realm={realm} />
        <div className="relative z-10 w-full max-w-md space-y-6 text-center">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border"
            style={{
              borderColor: `hsl(${realm.primaryAccent} / 0.3)`,
              background: `hsl(${realm.primaryAccent} / 0.08)`,
              color: `hsl(${realm.primaryAccent})`,
            }}
          >
            <svg
              className="h-8 w-8"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1
            className="font-heading text-xl font-bold"
            style={{ color: `hsl(${realm.primaryAccent})` }}
          >
            {title} — Complete
          </h1>
          <p className="text-sm text-white/70">
            {completedMessage || "You've completed this module. Great work!"}
          </p>
          {completedAction || (
            <Button onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen isolate overflow-x-hidden">
      <AmbientWorldLayer realm={realm} />

      {isQAOverride && (isLocked || isCompleted) && (
        <div className="sticky top-0 z-[60] border-b border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-center text-[11px] font-mono text-amber-300 backdrop-blur-md">
          QA preview — replays do not affect learner data
        </div>
      )}

      <ModuleRealmHeader
        realm={realm}
        title={title}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        onOpenCoach={() => setShowCoach(true)}
      />

      <main className="relative z-10 container mx-auto max-w-2xl px-3 py-5 sm:px-4 sm:py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={stepKey}
            initial={{ opacity: 0, y: 20, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.99 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <MissionPanel realm={realm}>{children}</MissionPanel>
          </motion.div>
        </AnimatePresence>
      </main>

      {showCoach && (
        <AIModuleTutorDrawer
          moduleId={title.toLowerCase().replace(/\s+/g, "_")}
          moduleContext={title}
          onClose={() => setShowCoach(false)}
        />
      )}
    </div>
  );
};

export default LunimarModuleShell;